import { Injectable } from "@angular/core";
import { IVTableCell } from "@app/directives/_layout/v-table/_models/IVTableCell";
import { IVTableHeader } from "@app/directives/_layout/v-table/_models/IVTableHeader";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { DynamicReportsService } from "@app/main/components/reports/_services/dynamic-reports.service";
import { ExportType } from "@app/main/components/reports/_services/dynamicReports.service";
import { ColumnDataType } from "@app/main/components/reports/enums/ColumnDataType";
import { IColumnHeader } from "@app/main/components/reports/models/IColumnHeader";
import { IReportDefinitionNew } from "@app/main/components/reports/models/IReportDefinition";
import { ReportParameterType } from "@app/main/components/reports/models/IReportParameters";
import { IReportTableRow } from "@app/main/components/reports/models/IReportRow";
import { IReportRowCell } from "@app/main/components/reports/models/IReportRowCell";
import { IReportVVTableDefinition } from "@app/main/components/reports/models/IReportTableDefinition";

export interface ITableExportParams {
    headers: IVTableHeader[];
    rows: IVTableRow[];

    exportType: ExportType;
    exportTitle: string;

    exportAsLandscape?: boolean;

    reportDefinition?: IReportDefinitionNew;
}

@Injectable({
    providedIn: 'root'
})
export class ExportService {
    constructor(private readonly dynamicReportService: DynamicReportsService) { }

    downloadTableAsReport(params: ITableExportParams): Promise<any> {

        return this.createReportDefinitionForExport(params).then(reportDefinition => {
            reportDefinition.TableDefinition.Rows?.forEach(row => delete row.tableRow);
            return this.downloadReportExport(reportDefinition, params.exportType);
        });
    }

    private createReportDefinitionForExport(params: ITableExportParams): Promise<IReportDefinitionNew> {
        if (params.reportDefinition) {
            return Promise.resolve(params.reportDefinition);
        }

        let headers: IVTableHeader[];
        let rows: IVTableRow[];

        if (params.headers && params.rows) {
            headers = params.headers;
            rows = params.rows;
        }

        if (!headers || !rows) {
            console.warn("No headers or rows to export");
            return Promise.resolve(null);
        }

        return new Promise((resolve) => {
            return this.dynamicReportService.getStandardColumns().then(standardColumns => {
                //Wrap it in a Promise to convert from IPromise
                resolve(this.createReportTableFromTable(headers, rows, standardColumns.stringColumn).then(tableDefinition => {

                    const reportDefinition: IReportDefinitionNew = {
                        Title: params.exportTitle,
                        TableDefinition: tableDefinition,
                        Parameters: {
                            CustomParameters: [
                                {
                                    Value: true,
                                    Active: true,
                                    ParameterType: ReportParameterType.HideParameterList
                                }]
                        },
                        Portrait: !params.exportAsLandscape
                    }

                    return reportDefinition;
                }));
            })
        });
    }

    private createReportTableFromTable(headers: IVTableHeader[], rows: IVTableRow[], stringColumn: IColumnHeader): Promise<IReportVVTableDefinition> {

        return this.createTableReportRows(headers, rows, stringColumn).then(reportRows => {
            const report: IReportVVTableDefinition = {
                Headers: this.createTableReportHeaders(headers, stringColumn),
                Rows: reportRows,
            };

            return report;
        });
    }

    private createTableReportRows(headers: IVTableHeader[], tableRows: IVTableRow[], stringColumn: IColumnHeader): Promise<IReportTableRow[]> {
        const rowPromises: Promise<any>[] = [];
        const reportRows: IReportTableRow[] = [];

        tableRows.forEach((row: IVTableRow, index) => {
            const rowPromise = this.createTableReportCells(headers, row.cells, stringColumn).then(cells => {

                const reportRow: IReportTableRow = {
                    Parameters: null,
                    Cells: cells,
                    HasData: true,
                    tableRow: row
                };

                reportRows.push(reportRow);
            });

            rowPromises.push(rowPromise);
        });

        return Promise.all(rowPromises).then(rows => {
            reportRows.sort((a, b) => {
                return tableRows.indexOf(a.tableRow) - tableRows.indexOf(b.tableRow);
            });

            return reportRows;
        });
    }

    private createTableReportHeaders(headers: IVTableHeader[], stringColumn: IColumnHeader): IColumnHeader[] {
        const reportHeaders: IColumnHeader[] = [];

        headers.forEach((header: IVTableHeader) => {
            if (header.excludeFromExport) {
                return;
            }

            const reportHeader = Object.assign({}, stringColumn);

            reportHeader.Title = header.title;
            reportHeader.Width = header.width ? header.width : undefined;

            reportHeaders.push(reportHeader);
        });

        return reportHeaders;
    }

    private createTableReportCells(headers: IVTableHeader[], cells: IVTableCell[], stringColumn: IColumnHeader): Promise<IReportRowCell[]> {
        const cellPromises: Promise<IReportRowCell>[] = [];

        cells.forEach((cell: IVTableCell, index) => {
            const header = headers[index];
            if (header.excludeFromExport) {
                return;
            }

            cellPromises.push(this.createReportCellFromTable(cell, stringColumn));
        });

        return Promise.all(cellPromises).then(cells => {
            return cells;
        });
    }

    private createReportCellFromTable(cell: IVTableCell, stringColumn: IColumnHeader): Promise<IReportRowCell> {

        const reportCell: IReportRowCell = {
            Data: String(cell.exportData) || cell.data,
            Contents: String(cell.exportData) || cell.data,
            CellDataType: ColumnDataType.String,
            DataSourceType: stringColumn.DataSourceType,
            DataSourceValue: stringColumn.DataSourceValue,
        };

        if (cell.exportDataMethod) {
            return cell.exportDataMethod().then(exportData => {
                reportCell.Data = exportData;
                reportCell.Contents = exportData;

                return reportCell;
            });
        }
        return Promise.resolve(reportCell);
    }

    private downloadReportExport(reportDefinition: IReportDefinitionNew, exportType: ExportType) {
        const documentUrl = this.getDocumentUrl(reportDefinition, exportType);

        if (documentUrl) {
            window.open(documentUrl);
            return;
        }

        return this.dynamicReportService.generate(reportDefinition, exportType);
    }

    private getDocumentUrl(reportDefinition: IReportDefinitionNew, exportType: ExportType) {
        switch (exportType) {
            case ExportType.PDF:
                return reportDefinition.reportPdfUrl;

            case ExportType.CSV:
                return reportDefinition.reportCsvUrl;

            case ExportType.Excel:
                return reportDefinition.reportExcelUrl;
        }
    }
}
